import { parseLocalDate } from '../../lib/dateUtils';

export const STATUS_STYLES = {
  planeado: 'border-amber-500/30 bg-amber-500/10 text-amber-600',
  en_curso: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600',
  finalizado: 'border-slate-500/30 bg-slate-500/10 text-slate-600',
  cancelado: 'border-rose-500/30 bg-rose-500/10 text-rose-600'
};

export const STATUS_LABELS = {
  planeado: 'Próximo',
  en_curso: 'En curso',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado'
};

export const FORMATO_LABELS = {
  liga: 'Liga',
  eliminatoria: 'Eliminatoria',
  mixto: 'Mixto',
  amistoso: 'Amistoso'
};

// Estatus de una inscripcion del club a un torneo (#3 - solicitud + aprobacion).
export const INSCRIPCION_STATUS_STYLES = {
  solicitada: 'border-amber-500/30 bg-amber-500/10 text-amber-600',
  aceptada: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600',
  rechazada: 'border-rose-500/30 bg-rose-500/10 text-rose-600',
  activo: 'border-slate-500/30 bg-slate-500/10 text-slate-600'
};

export const INSCRIPCION_STATUS_LABELS = {
  solicitada: 'Solicitada',
  aceptada: 'Aceptada',
  rechazada: 'Rechazada',
  activo: 'Activa'
};

// Ojo: 'activo' NO se mapea a 'en_curso'. Es un valor legado ambiguo (significa "registro
// activo", no "torneo en juego") y cae al calculo por fechas, que es mas fiel.
const STATUS_ALIASES = {
  'en curso': 'en_curso',
  programado: 'planeado',
  proximo: 'planeado',
  próximo: 'planeado',
  pendiente: 'planeado',
  terminado: 'finalizado',
  cerrado: 'finalizado',
  completado: 'finalizado'
};

// Estados que el usuario elige explicitamente y cierran el torneo.
const ESTADOS_CERRADOS = ['finalizado', 'cancelado'];

export const rawStatus = (torneo) => (torneo?.estatus || '').toString().trim().toLowerCase();

// El estatus guardado es la verdad cuando se reconoce. Si viene un valor legado o vacio,
// lo deducimos de las fechas para no mostrar nunca un enum crudo ni dejar el torneo
// fuera de todos los filtros. SOLO para mostrar: las acciones usan isCerrado().
export const resolveStatus = (torneo) => {
  const raw = rawStatus(torneo);
  const mapped = STATUS_ALIASES[raw] || raw;
  if (STATUS_LABELS[mapped]) return mapped;

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const inicio = parseLocalDate(torneo?.fecha_inicio);
  const fin = parseLocalDate(torneo?.fecha_fin);
  if (fin && fin < hoy) return 'finalizado';
  if (inicio && inicio > hoy) return 'planeado';
  if (inicio) return 'en_curso';
  return 'planeado';
};

// Para habilitar/ocultar acciones: se basa en lo GUARDADO, nunca en lo deducido, para que
// un torneo con estatus legado siga pudiendo finalizarse y limpiar el dato.
export const isCerrado = (torneo) => {
  const mapped = STATUS_ALIASES[rawStatus(torneo)] || rawStatus(torneo);
  return ESTADOS_CERRADOS.includes(mapped);
};

export const ETAPA_LABELS = {
  borrador: 'Borrador', inscripciones: 'Inscripciones', programacion: 'Programación',
  en_curso: 'En curso', finalizado: 'Finalizado', cancelado: 'Cancelado', suspendido: 'Suspendido',
};
export const ETAPA_STYLES = {
  borrador: 'border-slate-500/30 bg-slate-500/10 text-slate-600',
  inscripciones: 'border-sky-500/30 bg-sky-500/10 text-sky-600',
  programacion: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-600',
  en_curso: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600',
  finalizado: 'border-slate-500/30 bg-slate-500/10 text-slate-600',
  cancelado: 'border-rose-500/30 bg-rose-500/10 text-rose-600',
  suspendido: 'border-amber-500/30 bg-amber-500/10 text-amber-600',
};
const ETAPAS = Object.keys(ETAPA_LABELS);
export const etapaActual = (torneo) => {
  const e = (torneo?.etapa || '').toString().trim().toLowerCase();
  if (ETAPAS.includes(e)) return e;
  const r = resolveStatus(torneo); // planeado | en_curso | finalizado | cancelado
  return r === 'planeado' ? 'borrador' : r; // en_curso/finalizado/cancelado ya son etapas válidas
};
export const esArchivado = (torneo) => torneo?.activo === false;
// Historial (lado club): archivado o en una etapa terminal.
export const esHistorial = (torneo) => {
  const e = etapaActual(torneo);
  return esArchivado(torneo) || e === 'finalizado' || e === 'cancelado';
};
// Acciones del club: solo inscribir en 'inscripciones' y solicitar cambios de roster en
// 'en_curso'. Si el torneo esta archivado, ambas quedan bloqueadas.
export const accionesClubPermitidas = (torneo) => {
  if (esArchivado(torneo)) return { inscribir: false, cambiosRoster: false };
  const e = etapaActual(torneo);
  return { inscribir: e === 'inscripciones', cambiosRoster: e === 'en_curso' };
};

// ---------------------------------------------------------------------------
// El filtro de la lista, por ETAPA.
//
// Antes filtraba por `estatus`, que NO es otro estado: es un espejo grueso de la etapa
// que la base mantiene sincronizado (`_estatus_espejo`, y `set_torneo_etapa` escribe las
// dos columnas en la misma sentencia). Filtrar por el espejo metia bajo "Proximo" a un
// torneo en borrador, uno en inscripciones y uno en programacion -- que para el
// organizador son tres momentos distintos, y es justo lo que quiere separar.
//
// El orden es el del ciclo de vida, no alfabetico: un chip que se salta un paso obliga a
// leer la fila entera para saber donde va cada torneo.
export const ETAPAS_ORDEN = [
  'borrador', 'inscripciones', 'programacion', 'en_curso', 'suspendido', 'finalizado', 'cancelado',
];

// `borrador` es el default de la columna en la base: un torneo sin etapa esta empezando.
export const etapaDeTorneo = (torneo) => torneo?.etapa || 'borrador';

// Los chips que se muestran, con su conteo. Solo los que existen: un chip en 0 lleva a una
// lista vacia. Se conserva el que este activo aunque quede en 0, para no dejar al usuario
// mirando un filtro que ya no puede quitar.
export const chipsDeEtapa = (torneos, filtroActual = 'todos') => {
  const lista = torneos || [];
  const cuenta = new Map();
  lista.forEach((t) => {
    const e = etapaDeTorneo(t);
    cuenta.set(e, (cuenta.get(e) || 0) + 1);
  });

  const chips = [{ value: 'todos', label: 'Todos', count: lista.length }];
  ETAPAS_ORDEN.forEach((e) => {
    const n = cuenta.get(e) || 0;
    if (n > 0 || filtroActual === e) {
      chips.push({ value: e, label: ETAPA_LABELS[e] || e, count: n });
    }
  });
  // Con una sola etapa presente, "Todos | Borrador" no filtra nada: los dos chips
  // devuelven la misma lista. Hace falta que haya al menos DOS etapas que separar.
  return chips.length > 2 ? chips : [];
}
